import React, { useState } from 'react';
import { Image, View } from 'react-native';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRoute } from '@react-navigation/native';
import { api } from '../../lib/api';
import { errorMessage, fieldErrors } from '../../lib/errors';
import { todayISO } from '../../lib/date';
import { DOCUMENT_HINT, pickDocument } from '../../lib/picker';
import { appendFile, type UploadFile } from '../../lib/upload';
import { Alert, Button, Card, DateField, EmptyState, Grid, Loading, Screen, Select, Stack, Text, TextField, useTheme } from '../../ui';

const DOC_TYPES = [
  { key: 'id_proof', label: 'ID proof (Aadhaar / Passport)' },
  { key: 'address_proof', label: 'Address proof' },
  { key: 'pan', label: 'PAN card' },
  { key: 'education', label: 'Education certificates' },
  { key: 'other', label: 'Other (relieving letter etc.)' },
];

/** Public self-onboarding page (/onboarding/:token) — the backend emails this link to new joiners. */
export function Onboarding() {
  const token = String((useRoute().params as { token?: string } | undefined)?.token ?? '');
  const { colors } = useTheme();
  const q = useQuery({ queryKey: ['onboarding', token], queryFn: () => api.onboarding.get(token), retry: false, enabled: !!token });
  const [f, setF] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, UploadFile | null>>({});
  const [pickErr, setPickErr] = useState<string | null>(null);
  const submit = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => v && fd.append(k, v));
      Object.entries(files).forEach(([k, file]) => appendFile(fd, `documents[${k}]`, file));
      return api.onboarding.submit(token, fd);
    },
  });
  const fe = fieldErrors(submit.error);

  if (!token || q.isError)
    return (
      <Screen maxWidth={820}>
        <EmptyState icon="lock" title="This onboarding link is invalid or has expired" description={q.error ? errorMessage(q.error) : 'Ask HR to send you a new link.'} />
      </Screen>
    );
  if (q.isLoading || !q.data) return <Loading label="Loading your onboarding form…" />;
  if (submit.isSuccess)
    return (
      <Screen maxWidth={820}>
        <EmptyState icon="checkCircle" title="Thank you! Your details were submitted." description="HR will verify your documents and reach out if anything else is needed." />
      </Screen>
    );

  const emp = q.data.employee;
  const val = (k: string, fallback?: string | null) => f[k] ?? fallback ?? '';
  const set = (k: string) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const input = (k: string, label: string, o: { required?: boolean; placeholder?: string; fallback?: string | null; keyboard?: 'default' | 'email-address' | 'phone-pad' | 'number-pad'; caps?: 'characters' | 'none' | 'words' } = {}) => (
    <TextField key={k} label={label} required={o.required} placeholder={o.placeholder} value={val(k, o.fallback)} onChangeText={set(k)} error={fe[k]} keyboardType={o.keyboard} autoCapitalize={o.caps} />
  );
  const choose = async (key: string) => {
    setPickErr(null);
    try {
      const file = await pickDocument();
      if (file) setFiles((s) => ({ ...s, [key]: file }));
    } catch (e) {
      setPickErr(errorMessage(e));
    }
  };

  return (
    <Screen maxWidth={860} testID="onboarding">
      <View style={{ gap: 4 }}>
        <Text variant="h1">
          Welcome to {q.data.tenant.name}, {emp.first_name}!
        </Text>
        <Text variant="muted">Please complete your joining details. Fields marked * are required.</Text>
      </View>
      {submit.isError && !Object.keys(fe).length && <Alert tone="danger">{errorMessage(submit.error)}</Alert>}
      <Card title="Personal details">
        <Grid min={260} gap={14}>
          {input('first_name', 'First name', { required: true, fallback: emp.first_name, caps: 'words' })}
          {input('last_name', 'Last name', { fallback: emp.last_name, caps: 'words' })}
          <DateField label="Date of birth" required value={val('date_of_birth', emp.date_of_birth)} onChange={set('date_of_birth')} max={todayISO()} error={fe.date_of_birth} />
          <Select label="Gender" value={val('gender', emp.gender) || null} onChange={set('gender')} error={fe.gender} options={[{ value: 'male', label: 'Male' }, { value: 'female', label: 'Female' }, { value: 'other', label: 'Other' }]} />
          {input('phone', 'Mobile', { required: true, fallback: emp.phone, keyboard: 'phone-pad' })}
          {input('personal_email', 'Personal email', { fallback: emp.personal_email, keyboard: 'email-address', caps: 'none' })}
          {input('blood_group', 'Blood group', { fallback: emp.blood_group, caps: 'characters' })}
          <Select label="Marital status" value={val('marital_status', emp.marital_status) || null} onChange={set('marital_status')} error={fe.marital_status} options={['single', 'married', 'divorced', 'widowed'].map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }))} />
        </Grid>
        <View style={{ marginTop: 14 }}>
          <TextField label="Current address" multiline value={val('address', emp.address)} onChangeText={set('address')} error={fe.address} />
        </View>
      </Card>
      <Card title="Statutory & bank">
        <Grid min={260} gap={14}>
          {input('pan', 'PAN', { required: true, placeholder: 'ABCDE1234F', caps: 'characters' })}
          {input('aadhaar', 'Aadhaar', { placeholder: '12 digits', keyboard: 'number-pad' })}
          {input('uan', 'UAN (if any)', { placeholder: '12 digits', keyboard: 'number-pad' })}
          {input('esic_number', 'ESIC number (if any)')}
          {input('bank_name', 'Bank name', { required: true, caps: 'words' })}
          {input('bank_account_number', 'Account number', { required: true, keyboard: 'number-pad' })}
          {input('bank_ifsc', 'IFSC', { required: true, placeholder: 'HDFC0001234', caps: 'characters' })}
        </Grid>
      </Card>
      <Card title="Documents" subtitle={DOCUMENT_HINT}>
        <Stack gap={12}>
          {pickErr && <Alert tone="warning">{pickErr}</Alert>}
          {DOC_TYPES.map((d) => {
            const file = files[d.key];
            return (
              <View key={d.key} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: colors.border }}>
                {file && file.type.startsWith('image/') ? <Image source={{ uri: file.uri }} style={{ width: 40, height: 40, borderRadius: 6 }} /> : null}
                <View style={{ flex: 1, minWidth: 180 }}>
                  <Text weight="medium">{d.label}</Text>
                  <Text variant="small" numberOfLines={1}>
                    {file ? file.name : 'No file chosen'}
                  </Text>
                  {!!fe[`documents.${d.key}`] && (
                    <Text size={12} color={colors.danger}>
                      {fe[`documents.${d.key}`]}
                    </Text>
                  )}
                </View>
                <Button size="sm" icon="upload" title={file ? 'Replace' : 'Choose file'} onPress={() => choose(d.key)} />
                {file && <Button size="sm" variant="ghost" icon="x" accessibilityLabel={`Remove ${d.label}`} onPress={() => setFiles((s) => ({ ...s, [d.key]: null }))} />}
              </View>
            );
          })}
        </Stack>
      </Card>
      <View style={{ alignItems: 'flex-end' }}>
        <Button title="Submit details" variant="primary" size="lg" loading={submit.isPending} onPress={() => submit.mutate()} testID="onboarding-submit" />
      </View>
    </Screen>
  );
}
